<mat-drawer-container class="drawer-container h-full">
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <ng-container *ngTemplateOutlet="loadingTemplate"></ng-container>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="flex justify-center items-center p-8">
    <span class="section-content text-red-600">{{ error }}</span>
  </div>

  <!-- Editable Fields Section -->
  <div *ngIf="!loading && !error" class="flex w-full space-x-6 px-8">
    <div class="w-2/5 my-6">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <div class="flex flex-row">
        <span class="section-title mt-2">Editable Fields</span>
        <lib-cdw-button
          class="section-title mt-2 ml-auto content-center"
          mode="small"
          [customColor]="isJobRunning ? undefined : customButtonColor"
          fontSize="14px"
          (buttonClick)="editDevice()"
          [icon]="isJobRunning ? '' : 'edit'"
          fontFamily="Roboto"
          [outlined]="true"
          [disabled]="isJobRunning"
          [level]="isJobRunning ? 'secondary' : 'primary'"
        >
          <mat-progress-spinner
            *ngIf="isJobRunning"
            diameter="16"
            mode="indeterminate"
            class="inline-spinner">
          </mat-progress-spinner>
          {{ isJobRunning ? 'Saving...' : 'Edit' }}
        </lib-cdw-button>
      </div>
      <div class="info-grid space-y-5">
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Annotated asset ID</span>
          <span class="section-content">{{ getAnnotatedAssetId() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Annotated user</span>
          <span class="section-content">{{ getAnnotatedUser() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Annotated location</span>
          <span class="section-content">{{ getAnnotatedLocation() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Notes</span>
          <span class="section-content">{{ getNotes() }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Two columns per row sections -->
  <div *ngIf="!loading && !error" class="flex w-full mb-6 space-x-6 px-8">
    <div class="w-2/5 pr-4 my-6">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <span class="section-title mt-2">Hardware Details</span>
      <div class="info-grid space-y-5">
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Serial Number:</span>
          <span class="section-content">{{ deviceData?.serialNumber || 'N/A' }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Device OU</span>
          <span class="section-content">{{ getOrgUnitPath()}}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">CDW Device ID</span>
          <span class="section-content">{{ deviceData?.id || 'N/A' }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Model:</span>
          <span class="section-content">{{ getCpuModel() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">CPU Model</span>
          <span class="section-content">{{ getCpuModel() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">CPU Architecture</span>
          <span class="section-content">{{ getCpuArchitecture() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">CPU Max Clock Speed</span>
          <span class="section-content">{{ getCpuMaxClockSpeed() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Device Status</span>
          <span class="section-content">{{ getDeviceStatus() }}</span>
        </div>
        <div class="info-item flex flex-col gap-1">
          <span class="section-subtitle">Auto Update Expiration</span>
          <span class="section-content">{{ getAutoUpdateExpiration() | timezoneDateTime:(dateTimeSettings$ | async)! }}</span>
        </div>
      </div>
    </div>
    <div class="w-2/5 pl-4 my-6">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <span class="section-title mt-2">Operating System / Chrome Version</span>
      <div class="info-item flex flex-col gap-1">
        <div class="info-grid space-y-5">
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Chrome version</span>
            <span class="section-content">{{ getOsVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last stable Chrome version for model</span>
            <span class="section-content">{{ getLastStableChromeVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Maximum domain allowed for Chrome version</span>
            <span class="section-content">{{ getMaximumDomainAllowedChromeVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last OS update check</span>
            <span class="section-content">{{ getLastUpdateCheckDT() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last OS update</span>
            <span class="section-content">{{ getLastUpdateDT() | timezoneDateTime:(dateTimeSettings$ | async)! }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last reboot time</span>
            <span class="section-content">{{ getLastRebootTime() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last sync</span>
            <span class="section-content">{{ getLastSync() | timezoneDateTime:(dateTimeSettings$ | async)! }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Update State</span>
            <span class="section-content">{{ getUpdateState() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Target OS Version</span>
            <span class="section-content">{{ getTargetOsVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Update Check Time</span>
            <span class="section-content">{{ getUpdateCheckTime() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="!loading && !error" class="flex w-full space-x-6 px-8">
    <div class="w-2/5 pr-4 my-6">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <span class="section-title mt-2">Hardware Diagnostics and Telemetry Data</span>
      <div class="info-item flex flex-col gap-1">
        <div class="info-grid space-y-5">
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Boot mode</span>
            <span class="section-content">{{ getBootMode() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Platform version</span>
            <span class="section-content">{{ getPlatformVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">TPM firmware version</span>
            <span class="section-content">{{ getTpmFirmwareVersion() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Memory usage</span>
            <span class="section-content">{{ getMemoryUsage() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">CPU utilization</span>
            <span class="section-content">{{ getCpuUtilization() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">CPU Temperature</span>
            <span class="section-content">{{ getCpuTemperature() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Disk space usage</span>
            <span class="section-content">{{ getDiskUsage() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Storage Type</span>
            <span class="section-content">{{ getStorageType() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Storage Capacity</span>
            <span class="section-content">{{ getStorageCapacity() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Storage Read Speed</span>
            <span class="section-content">{{ getStorageReadSpeed() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Storage Write Speed</span>
            <span class="section-content">{{ getStorageWriteSpeed() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Battery type</span>
            <span class="section-content">{{ getBatteryType() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Battery Health</span>
            <span class="section-content">{{ getBatteryHealth() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last reported battery usage</span>
            <span class="section-content">{{ getLastReportedBatteryUsage() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Graphics adapter</span>
            <span class="section-content">{{ getGraphicsAdapter() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Displays</span>
            <span class="section-content">{{ getDisplays() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Design capacity</span>
            <span class="section-content">{{ getDesignCapacity() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Full charge capacity</span>
            <span class="section-content">{{ getFullChargeCapacity() }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="w-2/5 pl-4 my-6">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <span class="section-title mt-2">Networking</span>
      <div class="info-item flex flex-col gap-1">
        <div class="info-grid space-y-5">
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Mac address</span>
            <span class="section-content">{{ getMacAddress() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Ethernet Mac address</span>
            <span class="section-content">{{ getEthernetMacAddress() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">WiFi signal strength</span>
            <span class="section-content">{{ getWifiSignalStrength() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Last known IP address</span>
            <span class="section-content">{{ getLastKnownIpAddress() }}</span>
          </div>
          <div class="info-item flex flex-col gap-1">
            <span class="section-subtitle">Gateway</span>
            <span class="section-content">{{ getGateway() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Edit Drawer -->
  <lib-cdw-drawer
    drawerId="deviceEditDrawer"
    [toolbarTitle]="'Edit device'"
    [drawerWidth]="'600px'"
    [toolbarTitleBackground]="primaryWhite"
    [toolbarTitleColor]="gray300"
    toolbarTitleFontFamily="Source Sans 3"
    toolbarTitleFontSize="20px"
    toolbarTitleFontWeight="800"
    [hideHeaderBorder]="true"
    [interceptCloseButton]="true"
    (closeRequested)="attemptCloseDrawer()"
  >
    <form [formGroup]="editForm" class="drawer-form-container" (ngSubmit)="onFormSubmit($event)">
      <!-- Step 1: Edit Form -->
      <div class="drawer-form-content" *ngIf="currentStep === 1">
        <div class="flex flex-col gap-4 p-6">
          <div class="field-container">
            <mat-form-field appearance="outline" class="w-full" [class.field-invalid]="editForm.get('annotatedAssetId')?.invalid && (editForm.get('annotatedAssetId')?.dirty || editForm.get('annotatedAssetId')?.touched)" subscriptSizing="fixed">
              <mat-label>Asset ID</mat-label>
              <input matInput formControlName="annotatedAssetId" placeholder="Asset ID" [errorStateMatcher]="errorStateMatcher" />
            </mat-form-field>
            <div class="field-hint" [class.error]="isOverLimit('annotatedAssetId')">
              <span *ngIf="!isOverLimit('annotatedAssetId')">Max {{ FIELD_LIMITS.annotatedAssetId }} characters</span>
              <span *ngIf="isOverLimit('annotatedAssetId')" class="error-text">{{ TEXT_LIMIT_ERROR_MESSAGE }}</span>
              <span class="spacer"></span>
              <span [class.error-text]="isOverLimit('annotatedAssetId')">{{ editForm.get('annotatedAssetId')?.value?.length || 0 }}/{{ FIELD_LIMITS.annotatedAssetId }}</span>
            </div>
            <div *ngIf="hasInvalidCharsError()" class="field-error">
              {{ ASSET_ID_ERROR_MESSAGE }}
            </div>
          </div>

          <div class="field-container">
            <mat-form-field appearance="outline" class="w-full" [class.field-invalid]="isOverLimit('annotatedUser')" subscriptSizing="fixed">
              <mat-label>Annotated User</mat-label>
              <input matInput formControlName="annotatedUser" placeholder="Enter user name" [errorStateMatcher]="errorStateMatcher" />
            </mat-form-field>
            <div class="field-hint" [class.error]="isOverLimit('annotatedUser')">
              <span *ngIf="!isOverLimit('annotatedUser')">Max {{ FIELD_LIMITS.annotatedUser }} characters</span>
              <span *ngIf="isOverLimit('annotatedUser')" class="error-text">{{ TEXT_LIMIT_ERROR_MESSAGE }}</span>
              <span class="spacer"></span>
              <span [class.error-text]="isOverLimit('annotatedUser')">{{ editForm.get('annotatedUser')?.value?.length || 0 }}/{{ FIELD_LIMITS.annotatedUser }}</span>
            </div>
          </div>

          <div class="field-container">
            <mat-form-field appearance="outline" class="w-full" [class.field-invalid]="isOverLimit('annotatedLocation')" subscriptSizing="fixed">
              <mat-label>Annotated Location</mat-label>
              <input matInput formControlName="annotatedLocation" placeholder="Enter location" [errorStateMatcher]="errorStateMatcher" />
            </mat-form-field>
            <div class="field-hint" [class.error]="isOverLimit('annotatedLocation')">
              <span *ngIf="!isOverLimit('annotatedLocation')">Max {{ FIELD_LIMITS.annotatedLocation }} characters</span>
              <span *ngIf="isOverLimit('annotatedLocation')" class="error-text">{{ TEXT_LIMIT_ERROR_MESSAGE }}</span>
              <span class="spacer"></span>
              <span [class.error-text]="isOverLimit('annotatedLocation')">{{ editForm.get('annotatedLocation')?.value?.length || 0 }}/{{ FIELD_LIMITS.annotatedLocation }}</span>
            </div>
          </div>

          <div class="field-container">
            <mat-form-field appearance="outline" class="w-full" [class.field-invalid]="isOverLimit('notes')" subscriptSizing="fixed">
              <mat-label>Notes</mat-label>
              <textarea matInput formControlName="notes" placeholder="Enter notes" rows="3" [errorStateMatcher]="errorStateMatcher"></textarea>
            </mat-form-field>
            <div class="field-hint" [class.error]="isOverLimit('notes')">
              <span *ngIf="!isOverLimit('notes')">Max {{ FIELD_LIMITS.notes }} characters</span>
              <span *ngIf="isOverLimit('notes')" class="error-text">{{ TEXT_LIMIT_ERROR_MESSAGE }}</span>
              <span class="spacer"></span>
              <span [class.error-text]="isOverLimit('notes')">{{ editForm.get('notes')?.value?.length || 0 }}/{{ FIELD_LIMITS.notes }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Confirmation Summary -->
      <div class="drawer-form-content" *ngIf="currentStep === 2">
        <div class="p-6">
          <div class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Review Changes</h3>
            <p class="text-sm text-gray-600">Please review the following changes before confirming:</p>
          </div>

          <div class="space-y-4">
            <div *ngFor="let field of changedFields" class="border rounded-lg p-4 bg-gray-50">
              <div class="font-medium text-sm mb-2">{{ field.label }}</div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <span class="text-xs text-gray-500">Current Value:</span>
                  <div class="text-sm mt-1 p-2 bg-white rounded border border-gray-200">
                    {{ field.oldValue }}
                  </div>
                </div>
                <div>
                  <span class="text-xs text-gray-500">New Value:</span>
                  <div class="text-sm mt-1 p-2 bg-blue-50 rounded border border-blue-200">
                    {{ field.newValue }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="changedFields.length === 0" class="text-center py-8 text-gray-500">No changes detected</div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="drawer-form-actions border-t px-6 py-4">
        <!-- Step 1 Actions -->
        <div *ngIf="currentStep === 1" class="flex justify-between w-full">
          <lib-cdw-button
            mode="standard"
            level="secondary"
            fontSize="14px"
            fontFamily="Roboto"
            fontWeight="700"
            (buttonClick)="attemptCloseDrawer()"
            [disabled]="isSaving"
          >
            Cancel
          </lib-cdw-button>
          <lib-cdw-button
            mode="standard"
            level="primary"
            fontSize="14px"
            fontFamily="Roboto"
            fontWeight="700"
            (buttonClick)="nextStep()"
            [disabled]="editForm.invalid"
          >
            Next
          </lib-cdw-button>
        </div>

        <!-- Step 2 Actions -->
        <div *ngIf="currentStep === 2" class="flex justify-between w-full">
          <lib-cdw-button
            mode="standard"
            level="secondary"
            fontSize="14px"
            fontFamily="Roboto"
            fontWeight="700"
            (buttonClick)="previousStep()"
            [disabled]="isSaving"
          >
            Previous
          </lib-cdw-button>
          <div class="flex gap-4">
            <lib-cdw-button
              mode="standard"
              level="secondary"
              fontSize="14px"
              fontFamily="Roboto"
              fontWeight="700"
              (buttonClick)="attemptCloseDrawer()"
              [disabled]="isSaving"
            >
              Cancel
            </lib-cdw-button>
            <lib-cdw-button
              mode="standard"
              level="primary"
              fontSize="14px"
              fontFamily="Roboto"
              fontWeight="700"
              (buttonClick)="confirmAndSave()"
              [disabled]="isSaving || changedFields.length === 0"
            >
              {{ isSaving ? 'Saving...' : 'Confirm and Proceed' }}
            </lib-cdw-button>
          </div>
        </div>
      </div>
    </form>
  </lib-cdw-drawer>

  <!-- Reusable Loading Template -->
  <ng-template #loadingTemplate>
    <mat-progress-spinner
      diameter="80"
      mode="indeterminate"
      color="primary">
    </mat-progress-spinner>
    <p class="loading-text">Loading device information...</p>
  </ng-template>
</mat-drawer-container>
